import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignedIn, useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';
import AddressAutocomplete from '../components/AddressAutocomplete';

/**
 * JoinMarket: enter address to become a garage seller.
 * Geocodes address and assigns to city (creates city if needed).
 */
export default function JoinMarket() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [address, setAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = (address || '').trim();
    if (!trimmed) {
      setError('Enter your garage sale address.');
      return;
    }
    
    // Ensure Clerk is loaded and user is signed in
    if (!isLoaded) {
      setError('Please wait while we verify your sign-in status...');
      return;
    }
    
    if (!isSignedIn) {
      setError('Please sign in to create a garage sale.');
      return;
    }
    
    setError(null);
    setSubmitting(true);
    try {
      // Get token explicitly and verify it exists
      const token = await getToken();
      if (!token) {
        throw new Error('Unable to get authentication token. Please sign out and sign in again.');
      }
      
      const res = await api('/api/vendors/assign-address', {
        getToken,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401) {
          throw new Error('Authentication failed. Please sign out and sign in again.');
        }
        throw new Error(data.error || data.message || `Error ${res.status}`);
      }
      // Small delay to ensure backend transaction is committed before navigation
      await new Promise(resolve => setTimeout(resolve, 100));
      navigate('/profile', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SignedIn>
      <div className="flex flex-col gap-6">
        <h1 className="text-3xl font-semibold text-center">CREATE YOUR GARAGE SALE</h1>
        <p className="text-lg text-slate-600 dark:text-slate-400 text-center">
          Enter the address where your garage sale will take place. We'll automatically assign you to the appropriate city.
        </p>
        {error && (
          <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
            {error}
          </p>
        )}
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <label className="flex flex-col gap-2">
            <span className="text-lg font-medium">Garage Sale Address</span>
            <AddressAutocomplete
              value={address}
              onChange={setAddress}
              placeholder="Start typing your address..."
              disabled={submitting}
            />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Address autocomplete powered by Google Maps
            </p>
          </label>
          <button
            type="submit"
            disabled={submitting || !address.trim()}
            className="h-16 text-2xl w-full rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Creating…' : 'Create Garage Sale'}
          </button>
        </form>
      </div>
    </SignedIn>
  );
}
